/* screens/crm/LeadDrawer.jsx — карточка лида: Сведения / Заметки / История.
   - Сведения: правка контакта/суммы/источника + назначение ответственного, архив.
   - Заметки: лента (append-only) + добавление; старое не редактируется и не удаляется.
   - История: журнал перемещений с обязательными причинами.
   Перемещение между этапами делегируется родителю (onRequestMove → модалка причины). */
import React from 'react';
import { Drawer } from '../../components/ui/Drawer.jsx';
import { Tabs } from '../../components/patterns/Tabs.jsx';
import { Avatar } from '../../components/ui/Avatar.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Field, NativeSelect, NumberInput, TextInput, Textarea } from '../../components/ui/Field.jsx';
import { Select } from '../../components/ui/Select.jsx';
import { Pill } from '../../components/ui/Badge.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import { CustomFields } from './CustomFields.jsx';
import { LeadTransferModal } from './LeadTransferModal.jsx';
import * as crm from '../../lib/api/crm.js';

const { useState, useEffect, useCallback } = React;

function fmtDateTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (_) { return iso; }
}

const EMPTY_FORM = {
  client: '', company: '', email: '', phone: '', telegram: '', whatsapp: '', max: '',
  source: 'manual', source_detail: '', value: '',
};

function toForm(lead) {
  return {
    client: lead.client || '',
    company: lead.company || '',
    email: lead.email || '',
    phone: lead.phone || '',
    telegram: lead.telegram || '',
    whatsapp: lead.whatsapp || '',
    max: lead.max || '',
    source: lead.source || 'manual',
    source_detail: lead.source_detail || '',
    value: lead.value == null ? '' : String(lead.value),
  };
}

/* Метка привлечения заявки: «Канал: Название» самой свежей метки устройства —
   её пишет форма лендинга курса: `metadata.utm` = {label, items, responsible}
   (вывод `utm.services.describe_device_tags`). У заявок демо-стенда ключ `utm`
   другой формы — список, поэтому читаем строго `label` строкой, иначе пусто.
   Данные из публичной формы — только текстом. */
function utmLabelOf(meta) {
  const utm = meta && meta.utm && typeof meta.utm === 'object' ? meta.utm : null;
  return utm && typeof utm.label === 'string' ? utm.label.trim() : '';
}

/* AD-15: заявка с лендинга курса «Академии» — ссылка на курс, отметка оплаты и
   значения полей формы. metadata заполняет academy.views.PublicLandingFormView,
   `academy_purchase_id` ставит фулфилмент покупки (purchasing.py). Всё — ДАННЫЕ:
   рендерим текстом, ссылку собираем из slug (внутренний путь, не из metadata).
   Блок нужен ещё и как страховка: у лендинга, чьи поля не заведены в схеме
   воронки, значения лежат только в metadata.form — в «Дополнительно» их нет. */
function AcademyLeadBlock({ lead }) {
  const meta = (lead && lead.metadata && typeof lead.metadata === 'object') ? lead.metadata : {};
  const courseId = meta.academy_course_id;
  if (courseId == null) return null;
  const slug = typeof meta.academy_slug === 'string' ? meta.academy_slug : '';
  const title = (typeof meta.academy_course_title === 'string' && meta.academy_course_title.trim())
    || slug || `Курс #${courseId}`;
  const paid = meta.academy_purchase_id != null;
  // Показываем ТОЛЬКО те значения, которых нет в custom_fields: те уже отрисованы
  // ниже, в «Дополнительно», с человеческой подписью из схемы воронки. Без этого
  // фильтра карточка печатала «f_mnnkqaxs3u: Шеф-повар QA» рядом с «Должность:
  // Шеф-повар QA» — дубль, где у одного из двух вместо названия технический ключ.
  const known = (lead && lead.custom_fields && typeof lead.custom_fields === 'object')
    ? lead.custom_fields : {};
  const form = (meta.form && typeof meta.form === 'object' && !Array.isArray(meta.form))
    ? Object.entries(meta.form).filter(([k]) => !(k in known)) : [];
  const message = typeof meta.message === 'string' ? meta.message.trim() : '';
  const utm = utmLabelOf(meta);
  return (
    <>
      <div className="crm-section-label">Заявка с курса Академии</div>
      <div className="stack stack-6">
        <div className="cluster cluster-8" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
          {slug ? (
            <a className="btn btn--secondary btn--sm" href={`/course/${encodeURIComponent(slug)}`}
              target="_blank" rel="noopener noreferrer">
              <Icon name="external-link" size={14} />{title}
            </a>
          ) : <span className="t-label">{title}</span>}
          <Pill tone={paid ? 'ok' : 'neutral'} size="sm">{paid ? 'Курс оплачен' : 'Не оплачено'}</Pill>
        </div>
        {utm && <p className="t-caption">Метка привлечения: {utm}</p>}
        {message && <p className="t-caption">Комментарий из заявки: {message}</p>}
        {form.length > 0 && (
          <div className="stack stack-2">
            {form.map(([k, v]) => (
              <span key={k} className="t-caption text-mute">{k}: {String(v)}</span>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function LeadDrawer({ leadId, sources, staff = [], pipelines = [], presets = [], currentUser, reloadNonce, onClose, onRequestMove, onChanged, onTransferred }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('details');
  const [form, setForm] = useState(EMPTY_FORM);
  const [custom, setCustom] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [noteBody, setNoteBody] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await crm.getLead(leadId);
      setLead(data);
      setForm(toForm(data));
      setCustom(data.custom_fields || {});
      setDirty(false);
    } catch (e) {
      toast.show(e?.detail || 'Не удалось загрузить лид.', 'danger');
      onClose && onClose();
    } finally {
      setLoading(false);
    }
  }, [leadId]); // eslint-disable-line

  useEffect(() => { if (leadId) load(); }, [leadId, reloadNonce, load]);

  const setField = (k) => (v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };
  const setCustomFields = (v) => { setCustom(v); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        client: form.client.trim(), company: form.company,
        email: form.email, phone: form.phone,
        telegram: form.telegram, whatsapp: form.whatsapp, max: form.max,
        source: form.source, source_detail: form.source_detail,
        value: form.value === '' ? null : form.value,
        custom_fields: custom,
      };
      const updated = await crm.updateLead(leadId, body);
      setLead(updated); setForm(toForm(updated)); setCustom(updated.custom_fields || {}); setDirty(false);
      toast.show('Сохранено', 'accent');
      onChanged && onChanged();
    } catch (e) {
      toast.show(e?.detail || 'Не удалось сохранить.', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const setOwner = async (ownerId) => {
    try {
      const updated = await crm.updateLead(leadId, { owner: ownerId });
      setLead(updated);
      onChanged && onChanged();
    } catch (e) {
      toast.show(e?.detail || 'Не удалось изменить ответственного.', 'danger');
    }
  };

  const archive = async () => {
    const ok = await confirm({
      title: 'Архивировать лид?',
      body: 'Лид скроется с доски. Заметки и история сохранятся.',
      confirmLabel: 'В архив', tone: 'danger',
    });
    if (!ok) return;
    try {
      await crm.archiveLead(leadId);
      toast.show('Лид в архиве', 'accent');
      onChanged && onChanged();
      onClose && onClose();
    } catch (e) {
      toast.show(e?.detail || 'Не удалось архивировать.', 'danger');
    }
  };

  const addNote = async () => {
    const v = noteBody.trim();
    if (!v) return;
    setAddingNote(true);
    try {
      await crm.addNote(leadId, v);
      setNoteBody('');
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.show(e?.detail || 'Не удалось добавить заметку.', 'danger');
    } finally {
      setAddingNote(false);
    }
  };

  const transfer = async (payload) => {
    // бросит при ошибке → модалка покажет; setTransferOpen(false) только на успехе
    const result = await crm.transferLead(leadId, payload);
    setTransferOpen(false);
    toast.show(payload.copy ? 'Лид скопирован в другую воронку' : 'Лид перенесён в другую воронку', 'accent');
    if (onTransferred) onTransferred(result, payload);
    else onChanged && onChanged();
  };

  const notes = lead?.notes || [];
  const transitions = lead?.transitions || [];
  // Схема доп. полей — из воронки этого лида (а не активной на доске).
  const customFields = (lead && pipelines.find((p) => p.id === lead.pipeline)?.custom_fields) || [];

  const footer = (
    <>
      <Button variant="secondary" iconLeft="arrow-right" onClick={() => lead && onRequestMove(lead)} disabled={!lead}>
        Переместить
      </Button>
      <Button variant="ghost" iconLeft="layers" onClick={() => setTransferOpen(true)} disabled={!lead}>В воронку</Button>
      <Button variant="ghost" iconLeft="trash" onClick={archive} disabled={!lead}>В архив</Button>
      <div className="grow" />
      {tab === 'details' && (
        <Button variant="primary" loading={saving} disabled={!dirty || !form.client.trim()} onClick={save}>
          Сохранить
        </Button>
      )}
    </>
  );

  return (
    <Drawer open onClose={onClose} size="lg" title={lead ? lead.client : 'Лид'} footer={footer}>
      {loading || !lead ? (
        <div className="state state--loading"><Spinner size="lg" label="Загрузка…" /></div>
      ) : (
        <div className="stack stack-12">
          <div className="cluster cluster-8" style={{ alignItems: 'center' }}>
            <Pill tone={lead.stage_name ? 'info' : 'neutral'} size="sm">{lead.stage_name}</Pill>
            {lead.source_label && <Pill tone="neutral" size="sm">{lead.source_label}</Pill>}
          </div>

          <Tabs
            variant="line"
            value={tab}
            onChange={setTab}
            items={[
              { value: 'details', label: 'Сведения' },
              { value: 'notes', label: 'Заметки', count: notes.length },
              { value: 'history', label: 'История', count: transitions.length },
            ]}
          />

          {tab === 'details' && (
            <div className="stack stack-12">
              <AcademyLeadBlock lead={lead} />
              <Field label="Клиент" required>
                <TextInput value={form.client} onChange={(e) => setField('client')(e.target.value)} invalid={!form.client.trim()} />
              </Field>
              <Field label="Компания">
                <TextInput value={form.company} onChange={(e) => setField('company')(e.target.value)} />
              </Field>

              <div className="crm-section-label">Контакты</div>
              <div className="crm-grid2">
                <Field label="Email">
                  <TextInput type="email" value={form.email} onChange={(e) => setField('email')(e.target.value)} />
                </Field>
                <Field label="Телефон">
                  <TextInput value={form.phone} onChange={(e) => setField('phone')(e.target.value)} />
                </Field>
                <Field label="Telegram">
                  <TextInput value={form.telegram} onChange={(e) => setField('telegram')(e.target.value)} placeholder="@username" />
                </Field>
                <Field label="WhatsApp">
                  <TextInput value={form.whatsapp} onChange={(e) => setField('whatsapp')(e.target.value)} />
                </Field>
                <Field label="MAX">
                  <TextInput value={form.max} onChange={(e) => setField('max')(e.target.value)} />
                </Field>
              </div>

              {customFields.length > 0 && (
                <>
                  <div className="crm-section-label">Дополнительно</div>
                  <CustomFields defs={customFields} values={custom} onChange={setCustomFields} />
                </>
              )}

              <div className="crm-section-label">Сделка</div>
              <div className="crm-grid2">
                <Field label="Источник">
                  <NativeSelect value={form.source} onChange={(e) => setField('source')(e.target.value)} options={sources} />
                </Field>
                <Field label="Источник (детально)">
                  <TextInput value={form.source_detail} onChange={(e) => setField('source_detail')(e.target.value)} placeholder="лендинг, реклама…" />
                </Field>
              </div>
              <Field label="Сумма">
                <NumberInput value={form.value} onChange={setField('value')} unit="₽" min={0} />
              </Field>

              <Field label="Ответственный">
                <div className="cluster cluster-8" style={{ alignItems: 'center' }}>
                  {lead.owner_label && <Avatar src={lead.owner_avatar} label={lead.owner_label} size={28} />}
                  <div className="grow" style={{ minWidth: 0 }}>
                    <Select
                      value={lead.owner ? String(lead.owner) : ''}
                      onChange={(v) => setOwner(v ? Number(v) : null)}
                      options={[{ value: '', label: '— Не назначен —' },
                        ...staff.map((u) => ({ value: String(u.id), label: u.full_name }))]}
                      placeholder="Не назначен" searchable={staff.length > 6} />
                  </div>
                  {currentUser?.id && String(lead.owner) !== String(currentUser.id) && (
                    <Button size="sm" variant="ghost" onClick={() => setOwner(currentUser.id)}>На себя</Button>
                  )}
                </div>
              </Field>

              <p className="t-caption text-mute">Создан: {fmtDateTime(lead.created_at)}{lead.created_by_label ? ` · ${lead.created_by_label}` : ''}</p>
            </div>
          )}

          {tab === 'notes' && (
            <div className="stack stack-12">
              <div className="crm-note-composer">
                <Textarea value={noteBody} onChange={(e) => setNoteBody(e.target.value)} minRows={2} maxRows={8}
                  placeholder="Новая заметка: что узнали о клиенте…" />
                <div className="cluster cluster-8" style={{ alignItems: 'center' }}>
                  <span className="crm-immutable-hint"><Icon name="lock" size={13} />Заметки нельзя изменить или удалить</span>
                  <div className="grow" />
                  <Button variant="primary" size="sm" iconLeft="plus" loading={addingNote}
                    disabled={!noteBody.trim()} onClick={addNote}>Добавить</Button>
                </div>
              </div>
              {notes.length === 0 ? (
                <p className="text-dim">Пока нет заметок.</p>
              ) : (
                <div className="crm-notes">
                  {notes.map((n) => (
                    <div key={n.id} className="crm-note">
                      <div className="crm-note__head">
                        <span className="crm-note__by">
                          <Avatar src={n.author_avatar} label={n.author_label || 'Система'} size={24} />
                          <span className="crm-note__author">{n.author_label || 'Система'}</span>
                        </span>
                        <span className="crm-note__time">{fmtDateTime(n.created_at)}</span>
                      </div>
                      <div className="crm-note__body">{n.body}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'history' && (
            <div className="crm-history">
              {transitions.length === 0 ? (
                <p className="text-dim">История пуста.</p>
              ) : (
                transitions.map((t, i) => (
                  <div key={t.id} className="crm-trans">
                    <div className="crm-trans__rail">
                      <span className="crm-trans__dot" />
                      {i < transitions.length - 1 && <span className="crm-trans__line" />}
                    </div>
                    <div>
                      <div className="crm-trans__move">
                        {t.from_stage_name ? `${t.from_stage_name} → ${t.to_stage_name}` : `Вход: ${t.to_stage_name}`}
                      </div>
                      <div className="crm-trans__reason">{t.reason}</div>
                      <div className="crm-trans__meta" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {t.author_label && <Avatar src={t.author_avatar} label={t.author_label} size={18} />}
                        <span>{fmtDateTime(t.created_at)}{t.author_label ? ` · ${t.author_label}` : ''}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      <LeadTransferModal
        open={transferOpen}
        lead={lead}
        pipelines={pipelines}
        presets={presets}
        onCancel={() => setTransferOpen(false)}
        onConfirm={transfer}
      />
    </Drawer>
  );
}

export { LeadDrawer, AcademyLeadBlock, utmLabelOf };
